<template>
  <vs-popup
    title="Skills"
    color="primary"
    classContent="skills-popup__body"
    :active.sync="showModal"
    class="skills-popup"
  >
  <div class="skills-popup__content">
    <div class="skills-popup__content-selectors">
      <div class="skills-popup__content-selectors--path">
        <div class="type-field">
          <label class="vs-input--label">Domain</label>
          <v-select
            v-model="domainSelected"
            :clearable="false"
            placeholder="Select one"
            :options="filteredDomainOptions"
          />
        </div>
        <div class="type-field">
          <label class="vs-input--label">Subdomain</label>
          <v-select
            v-model="subdomainSelected"
            :clearable="false"
            placeholder="Select one"
            :disabled="!domainSelected"
            :options="filteredSubdomainOptions"
          />
        </div>
        <div class="type-field">
          <label class="vs-input--label">Skill Area</label>
          <v-select
            v-model="skillAreaSelected"
            :clearable="false"
            placeholder="Select one"
            :disabled="!subdomainSelected"
            :options="filteredSkillAreaOptions"
          />
        </div>
      </div>
      <div class="">
        <div class="type-field" v-if="filteredSkillsOptions && filteredSkillsOptions.length">
          <label class="vs-input--label">Skills</label>
          <ul class="skills-popup__skills-list">
            <li v-for="(skill, index) in filteredSkillsOptions" :key="index">
              <vs-checkbox
                v-model="skillsSelectedIds"
                :vs-value="skill.id"
              >
                {{ skill.name }}
              </vs-checkbox>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="skills-popup__content-selection">
      <span class="skills-popup__content-selection--title">Skills selected: {{ selectedSkillObjectsCache.length }}</span>
      <div style="overflow: scroll">
        <vs-chip
          v-for="(skill, index) in selectedSkillObjectsCache"
          :key="index"
          @click="skillsSelectedIds.splice(index, 1)"
          closable
          color="dark"
        >
          {{ skill.name }}
        </vs-chip>
      </div>
    </div>
  </div>
  <div class="skills-popup__actions">
    <span class="skills-popup__actions-text">Please remember to save before leaving <b>Edit</b> page.</span>
    <vs-button
      color="primary"
      icon-pack="feather"
      @click="addSkills"
      :disabled="!skillsSelectedIds.length"
    >
      Add Skills
    </vs-button>
  </div>
  </vs-popup>
</template>

<script>
import vSelect from 'vue-select'
import { mapActions } from 'vuex'
import CONSTANTS from '../constants';

export default {
  name: 'SkillsPopup',
  components: {
    'v-select': vSelect,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'skills-added'],
  data() {
    return {
      domainSelected: null,
      subdomainSelected: null,
      skillAreaSelected: null,
      skillsSelectedIds: [],
      selectedSkillObjectsCache: [],
      filteredDomainOptions: [],
      filteredSubdomainOptions: [],
      filteredSkillAreaOptions: [],
      filteredSkillsOptions: [],
    }
  },
  computed: {
    showModal: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    currentTaxonomyPath() {
      let path = ''
      if (this.domainSelected) {
        path += this.domainSelected.label
      }
      if (this.subdomainSelected) {
        path += ` > ${this.subdomainSelected.label}`
      }
      if (this.skillAreaSelected) {
        path += ` > ${this.skillAreaSelected.label}`
      }
      return path
    },
  },
  watch: {
    domainSelected(newDomain) {
      this.subdomainSelected = null
      if (newDomain) {
        const response = this.getFilteredDepthOptions(CONSTANTS.SKILL_LEVELS.SUBDOMAIN)
        response.then(data => {
          this.filteredSubdomainOptions = data && data.results && data.results.map(item => ({label: item.name, value: item.id})) || []
        })
      } else {
        this.filteredSubdomainOptions = []
      }
    },
    subdomainSelected(newSubdomain) {
      this.skillAreaSelected = null
      if (newSubdomain) {
        const response = this.getFilteredDepthOptions(CONSTANTS.SKILL_LEVELS.SKILL_AREA)
        response.then(data => {
          this.filteredSkillAreaOptions = data && data.results && data.results.map(item => ({label: item.name, value: item.id})) || []
        })
      } else {
        this.filteredSkillAreaOptions = []
      }
    },
    skillAreaSelected(newSkillArea) {
      if (newSkillArea) {
        const response = this.getFilteredDepthOptions(CONSTANTS.SKILL_LEVELS.SKILL)
        response.then(data => {
          this.filteredSkillsOptions = data && data.results || []
        })
      } else {
        this.filteredSkillsOptions = []
      }
    },
    skillsSelectedIds(newSkills) {
      const byId = new Map([...this.filteredSkillsOptions, ...this.selectedSkillObjectsCache].map(s => [s.id, s]))
      this.selectedSkillObjectsCache = newSkills.map(id => byId.get(id)).filter(Boolean)
    },
  },
  async created() {
    const response = await this.getFilteredDepthOptions(CONSTANTS.SKILL_LEVELS.DOMAIN)
    this.filteredDomainOptions = response && response.results && response.results.map(item => ({label: item.name, value: item.id})) || []
  },
  methods: {
    ...mapActions('taxonomy', [
      'fetchTaxonomies',
    ]),
    async getFilteredDepthOptions(depth) {
      try {
        this.$vs.loading()
        const params = this.currentTaxonomyPath ? { depth: depth.toString(), taxonomyPath: this.currentTaxonomyPath } : { depth: depth.toString() }
        const options = await this.fetchTaxonomies(params)
        return options
      } catch (error) {
        this.showError(error)
      } finally {
        this.$vs.loading.close()
      }
    },
    showError(error) {
      this.$vs.notify({
        title: 'Error',
        text: error.message || 'An error occurred',
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'danger',
      })
    },
    clearData() {
      this.domainSelected = null
      this.subdomainSelected = null
      this.skillAreaSelected = null
      this.skillsSelectedIds = []
      this.selectedSkillObjectsCache = []
      this.filteredSubdomainOptions = []
      this.filteredSkillAreaOptions = []
      this.filteredSkillsOptions = []
    },
    addSkills() {
      this.$emit('skills-added', this.selectedSkillObjectsCache)
      this.showModal = false
      this.clearData()
    }
  },
}
</script>

<style lang="scss">
.skills-popup .vs-popup {
 width: 1058px
}

.skills-popup {
  &__body {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  &__content {
    display: flex;
    height: 377px;

    .type-field {
      flex: 1 1 0;
    }
  }
  &__content-selectors {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding-right: 20px;
    border-right: 1px solid #e5e7eb;

    &--path {
      display: flex;
      gap: 10px;
      margin-bottom: 20px;
    }
  }
  &__content-selection {
    display: flex;
    flex: 0.3 1 0;
    flex-direction: column;
    margin-left: 10px;
    padding-top: 20px;
    gap: 10px;


    &--title {
      display: flex;
      justify-content: center;
    }
  }
  &__skills-list {
    margin-top: 10px;

    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    max-height: 300px;

    li {
      list-style: none;
      margin-bottom: 5px;
    }
  }
  &__actions {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }
}
</style>
